import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { hasRole, useApi, useApp } from "../context/AppContext";
import { LEGACY_POSES, POSES, SHAPES, SIZE_CLASSES, STATUS_TONE, loadCatalogueContext } from "../lib/catalogue";
import { ago } from "../lib/fleet";
import { nameOf, useProductNames } from "../lib/products";

// One product (docs/tasks/35, Products): what it is (name, shape, size, weight,
// editable), its photos by pose with a per-slot replacement and the processing
// steps, its training on every cabinet type with the enrolment sets behind it,
// and where it is placed. The capture app remains where photos are shot and
// training visits are run.

function Details({ product, onSaved, mayWrite }) {
  const api = useApi();
  const [f, setF] = useState(null);
  const [msg, setMsg] = useState(null);
  useEffect(() => {
    const d = product.dimensions_mm || [];
    setF({ product_name: product.product_name || "", brand: product.brand || "", product_type: product.product_type || "",
      size_class: product.size_class || "", L: d[0] ?? "", W: d[1] ?? "", H: d[2] ?? "", weight_g: product.weight_g ?? "" });
  }, [product]);
  if (!f) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const pickClass = (e) => {
    const k = e.target.value;
    const tpl = SIZE_CLASSES[k]?.[1];
    setF({ ...f, size_class: k, ...(tpl && !f.L ? { L: tpl[0], W: tpl[1], H: tpl[2] } : {}) });
  };
  const save = async (e) => {
    e.preventDefault(); setMsg(null);
    const dims = [f.L, f.W, f.H].map((x) => (x === "" ? null : Number(x)));
    const body = { product_name: f.product_name, brand: f.brand, product_type: f.product_type || null, size_class: f.size_class || null,
      weight_g: f.weight_g === "" ? null : Number(f.weight_g), ...(dims.every((x) => x != null && x > 0) ? { dimensions_mm: dims } : {}) };
    try { await api.updateProduct(product.id, body); setMsg({ text: "Saved." }); onSaved(); }
    catch (ex) { setMsg({ bad: true, text: String(ex.message || ex) }); }
  };
  const ro = !mayWrite;
  return (
    <form className="card space-y-3" onSubmit={save}>
      <h2 className="text-sm font-semibold">What it is</h2>
      <div className="grid gap-3 md:grid-cols-4">
        <label className="text-xs md:col-span-2">Name<input className="input" disabled={ro} value={f.product_name} onChange={set("product_name")} placeholder={product.auto_detected_name || ""} /></label>
        <label className="text-xs">Brand<input className="input" disabled={ro} value={f.brand} onChange={set("brand")} /></label>
        <label className="text-xs">Shape
          <select className="input" disabled={ro} value={f.product_type} onChange={set("product_type")}>
            <option value="">not set</option>
            {Object.entries(SHAPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label className="text-xs md:col-span-2">Size class
          <select className="input" disabled={ro} value={f.size_class} onChange={pickClass}>
            <option value="">not set</option>
            {Object.entries(SIZE_CLASSES).map(([k, [v, d]]) => <option key={k} value={k}>{v} ({d.join("×")} mm)</option>)}
          </select>
        </label>
        <label className="text-xs">Size, mm (L × W × H)
          <span className="flex gap-1">
            {["L", "W", "H"].map((k) => <input key={k} className="input" disabled={ro} type="number" min="1" value={f[k]} onChange={set(k)} />)}
          </span>
        </label>
        <label className="text-xs">Weight, g (one unit)<input className="input" disabled={ro} type="number" min="0" step="0.1" value={f.weight_g} onChange={set("weight_g")} /></label>
      </div>
      {f.size_class === "flat_small" && <p className="text-xs text-amber-700">Small and flat: keep it away from the row by the door, where a hand hides it (the layout warns about it).</p>}
      {!f.product_type && <p className="text-xs text-amber-700">Without a shape the pose script is unknown, and the photo slots below are generic.</p>}
      {mayWrite && <div className="flex items-center gap-2"><button className="btn btn-primary text-xs" type="submit">Save</button>{msg && <span className={`text-xs ${msg.bad ? "text-rose-700" : "text-emerald-700"}`}>{msg.text}</span>}</div>}
      <p className="text-xs text-slate-500">
        EAN <span className="font-mono">{product.ean_code}</span>
        {product.catalog?.source ? ` · catalogue ${product.catalog.source}` : ""}
        {product.owner_network_id ? ` · owned by network ${product.owner_network_id.slice(0, 8)} (edits through the app are theirs)` : ""}
        {` · updated ${ago(product.updated_at)}`}
      </p>
    </form>
  );
}

function Photos({ product, onChanged, mayWrite }) {
  const api = useApi();
  const [raw, setRaw] = useState([]);
  const [processed, setProcessed] = useState([]);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const [report, setReport] = useState(null);
  const load = () => {
    api.listProductImages(product.id, "raw").then((d) => setRaw(Array.isArray(d) ? d : d?.items || [])).catch(() => setRaw([]));
    api.listProductImages(product.id, "processed").then((d) => setProcessed(Array.isArray(d) ? d : d?.items || [])).catch(() => setProcessed([]));
  };
  useEffect(load, [api, product.id, product.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const training = raw.filter((x) => x.kind === "training");
  const tests = raw.filter((x) => x.kind === "test");
  const script = POSES[product.product_type] || [];
  // Slots: the shape's script, then any pose the product was shot with beyond it.
  const slots = useMemo(() => {
    const out = script.map(([pos, label], i) => ({ index: i + 1, position: pos, label }));
    for (const ph of training) {
      if (!out.some((s) => s.index === ph.index)) out.push({ index: ph.index, position: ph.position, label: LEGACY_POSES[ph.position] || ph.position || `photo ${ph.index}` });
    }
    if (!out.length) out.push({ index: 1, position: "front", label: "Front" });
    return out.sort((a, b) => a.index - b.index);
  }, [script, training]);

  const replace = async (slot, file, kind = "training") => {
    if (!file) return;
    setBusy(`upload ${slot.index}`); setMsg(null);
    try {
      await api.uploadProductPhotoSingle(product.id, { file, kind, index: slot.index, position: kind === "training" ? slot.position : undefined });
      setMsg({ text: `${kind === "test" ? "Test photo" : slot.label} replaced. Process the photos again for it to count.` });
      load(); onChanged();
    } catch (ex) { setMsg({ bad: true, text: String(ex.message || ex) }); } finally { setBusy(null); }
  };
  const process = async () => {
    setBusy("process"); setMsg(null); setReport(null);
    try {
      await api.preprocessProduct(product.id);
      const v = await api.validateProduct(product.id);
      setReport(v);
      setMsg({ text: v.status === "validated" ? "Processed and validated." : "Processed; the photo check failed - see below.", bad: v.status !== "validated" });
      load(); onChanged();
    } catch (ex) { setMsg({ bad: true, text: String(ex.message || ex) }); } finally { setBusy(null); }
  };
  const suggest = async () => {
    setBusy("name"); setMsg(null);
    try {
      const s = await api.suggestProductName(product.id);
      setMsg({ text: s.suggested_name ? `Suggested: "${s.suggested_name}"${s.brand ? ` (${s.brand})` : ""} from ${s.source}${s.auto_promoted ? " - saved as the name" : ""}.` : "No name could be read." });
      onChanged();
    } catch (ex) { setMsg({ bad: true, text: String(ex.message || ex) }); } finally { setBusy(null); }
  };
  const find = (list, idx, kind = "training") => list.find((x) => x.kind === kind && x.index === idx);
  const Thumb = ({ img, label }) => img?.url ? (
    <a href={img.url} target="_blank" rel="noreferrer" title={label}><img src={img.url} alt={label} className="h-24 w-24 rounded border border-slate-200 bg-white object-contain" /></a>
  ) : <div className="flex h-24 w-24 items-center justify-center rounded border border-dashed border-slate-300 text-[10px] text-slate-400">{label}</div>;
  return (
    <div className="card space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold">Photos</h2>
          <p className="text-xs text-slate-500">
            {SHAPES[product.product_type] ? `The capture app's ${script.length}-shot script for ${SHAPES[product.product_type]}.` : "Set the shape to see its pose script."}
            {" "}Raw as shot, and processed (background removed) as the gallery uses it. Status: <strong>{product.status}</strong>
            {product.last_validation_score != null ? `, check score ${Math.round(product.last_validation_score * 100)}%` : ""}.
          </p>
        </div>
        {mayWrite && (
          <div className="flex gap-2">
            <button className="btn btn-primary text-xs" disabled={!!busy || !training.length} onClick={process}>{busy === "process" ? "Processing…" : "Process the photos"}</button>
            <button className="btn btn-secondary text-xs" disabled={!!busy || !training.length} onClick={suggest}>{busy === "name" ? "Reading…" : "Read the name"}</button>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-4">
        {slots.map((s) => (
          <div key={s.index} className="space-y-1 text-xs">
            <div className="font-medium">{s.index}. {s.label}</div>
            <div className="flex gap-1"><Thumb img={find(raw, s.index)} label="raw" /><Thumb img={find(processed, s.index)} label="processed" /></div>
            {mayWrite && (
              <label className="cursor-pointer text-sky-700 hover:underline">
                {find(raw, s.index) ? "replace" : "upload"}
                <input type="file" accept="image/jpeg,image/png,image/heic" className="hidden" disabled={!!busy} onChange={(e) => replace(s, e.target.files?.[0])} />
              </label>
            )}
          </div>
        ))}
        <div className="space-y-1 text-xs">
          <div className="font-medium">Test photo (optional)</div>
          <div className="flex gap-1"><Thumb img={tests[0]} label="raw" /><Thumb img={processed.find((x) => x.kind === "test")} label="processed" /></div>
          {mayWrite && (
            <label className="cursor-pointer text-sky-700 hover:underline">{tests[0] ? "replace" : "upload"}
              <input type="file" accept="image/jpeg,image/png,image/heic" className="hidden" disabled={!!busy} onChange={(e) => replace({ index: 1, label: "Test photo" }, e.target.files?.[0], "test")} />
            </label>
          )}
        </div>
      </div>
      {msg && <p className={`text-xs ${msg.bad ? "text-rose-700" : "text-emerald-700"}`}>{msg.text}</p>}
      {report && (
        <div className="rounded border border-slate-200 bg-slate-50 p-2 text-xs">
          <div className="font-medium">Photo check: {report.status} · score {report.score != null ? Math.round(report.score * 100) : "—"}%</div>
          {(report.recommendations || []).length > 0 && <ul className="list-disc pl-5">{report.recommendations.map((r, i) => <li key={i}>{typeof r === "string" ? r : JSON.stringify(r)}</li>)}</ul>}
        </div>
      )}
      <p className="text-xs text-slate-500">Photos teach the gallery the product's card image, name and colour; recognition in a cabinet comes from its training visits there (below).</p>
    </div>
  );
}

function Training({ product, ctx }) {
  const api = useApi();
  const names = useProductNames(api);
  const { user } = useApp();
  const [sets, setSets] = useState({});
  const mayToggle = hasRole(user, "tenant_admin", "ml_operator");
  const loadSets = () => {
    Promise.all((ctx?.types || []).map((t) => api.listEnrolmentSets(t.id).then((all) => [t.id, all.filter((x) => x.product_uuid === product.id)]).catch(() => [t.id, []])))
      .then((pairs) => setSets(Object.fromEntries(pairs)));
  };
  useEffect(loadSets, [api, ctx, product.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = async (typeId, s) => {
    if (!window.confirm(s.active ? "Switch this set off? Its rows leave the next gallery built for the type (the rows are kept)." : "Switch this set back on?")) return;
    try { await api.setEnrolmentSetActive(typeId, s.id, !s.active); loadSets(); } catch (ex) { window.alert(ex.message || ex); }
  };
  if (!ctx) return <div className="card text-sm text-slate-500">Loading the training…</div>;
  return (
    <div className="card space-y-3">
      <div>
        <h2 className="text-sm font-semibold">Training on each cabinet type</h2>
        <p className="text-xs text-slate-500">Rows taught in training visits belong to a cabinet type: they transfer between cabinets of one type, never between types or platforms. Visits are run from the capture app; a type's cabinets get the rows with their next Update shelves.</p>
      </div>
      {ctx.types.length === 0 ? <p className="text-sm text-slate-500">No cabinet types.</p> : ctx.types.map((t) => {
        const s = ctx.statusByType[t.id]?.[product.id];
        const st = s?.status || "untrained";
        const mine = sets[t.id] || [];
        return (
          <div key={t.id} className="rounded border border-slate-200 p-3 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <Link className="font-medium text-sky-700 hover:underline" to={`/types/${t.id}`}>{t.name}</Link>
              <span className={`rounded px-1.5 py-0.5 text-xs ${STATUS_TONE[st]}`}>{st}</span>
              {s?.last_trained_at ? <span className="text-xs text-slate-500">last trained {ago(s.last_trained_at)}</span> : null}
            </div>
            {s ? (
              <dl className="mt-2 grid grid-cols-[12rem_1fr] gap-y-0.5 text-xs">
                <dt className="text-slate-500">Rows by camera</dt><dd className="font-mono">{Object.entries(s.rows_by_camera || {}).map(([c, n]) => `camera ${c}: ${n}`).join(" · ")}</dd>
                <dt className="text-slate-500">Required cameras</dt><dd>{(s.required_cameras || []).join(", ")}{s.required_cameras?.length > 1 ? " (small product: the bottom witness too)" : ""}</dd>
                <dt className="text-slate-500">Separability</dt><dd>{s.separability != null ? s.separability.toFixed(3) : "—"}{s.confident === false ? <span className="text-amber-700"> · weak: a look-alike is close</span> : ""}</dd>
                <dt className="text-slate-500">Nearest look-alike</dt><dd>{s.nearest_product ? <Link className="text-sky-700 hover:underline" to={`/products/${s.nearest_product}`}>{nameOf(names, s.nearest_product)}</Link> : "—"}</dd>
                <dt className="text-slate-500">Shelf coverage</dt><dd>{s.cells_covered}/{s.cells_total} ({s.coverage_pct}%)</dd>
              </dl>
            ) : <p className="mt-1 text-xs text-slate-500">No training visit on this type yet.</p>}
            {mine.length > 0 && (
              <table className="table mt-2 text-xs">
                <thead><tr><th>Set</th><th>Camera</th><th>Shelf</th><th>Rows</th><th>Encoder</th><th>From</th><th>Made</th><th>Active</th></tr></thead>
                <tbody>
                  {mine.sort((a, b) => (b.created_at || 0) - (a.created_at || 0)).map((x) => (
                    <tr key={x.id} className={x.active ? "" : "text-slate-400"}>
                      <td className="font-mono">{x.id.slice(0, 8)}</td><td>{x.camera}</td><td>{x.shelf}</td><td>{x.n_rows}</td>
                      <td className="font-mono">{x.encoder_version}</td>
                      <td>{x.source}{x.source_session ? <span className="font-mono"> {String(x.source_session).slice(0, 8)}</span> : ""}</td>
                      <td>{x.created_at ? ago(x.created_at) : "—"}</td>
                      <td>{mayToggle ? <button className="text-sky-700 hover:underline" onClick={() => toggle(t.id, x)}>{x.active ? "on - switch off" : "off - switch on"}</button> : (x.active ? "on" : "off")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Placements({ product, ctx }) {
  if (!ctx) return null;
  if (ctx.partial) return <div className="card text-sm text-slate-500">Reading the cabinets' layouts…</div>;
  const shelves = ctx.placements[product.id] || [];
  const trays = ctx.trays[product.id] || [];
  return (
    <div className="card text-sm">
      <h2 className="mb-2 text-sm font-semibold">Where it is placed</h2>
      {shelves.length === 0 && trays.length === 0 ? <p className="text-slate-500">On no cabinet's layout.</p> : (
        <ul className="space-y-1 text-xs">
          {shelves.map((x, i) => <li key={`s${i}`}><Link className="text-sky-700 hover:underline" to={`/cabinets/${x.deviceId}?tab=layout`}>{x.name}</Link> · shelf {x.shelf}{x.shelf === 1 ? " (by the door)" : ""}</li>)}
          {trays.map((x, i) => <li key={`t${i}`}><Link className="text-sky-700 hover:underline" to={`/cabinets/${x.deviceId}?tab=weight`}>{x.name}</Link> · weight tray {x.tray}{x.unit_g ? ` · ${x.unit_g} g each` : ""}</li>)}
        </ul>
      )}
    </div>
  );
}

export function ProductDetailPage() {
  const { id } = useParams();
  const api = useApi();
  const { user } = useApp();
  const [product, setProduct] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [err, setErr] = useState(null);
  const mayWrite = hasRole(user, "tenant_admin", "product_manager");
  const load = () => api.getProduct(id).then(setProduct).catch((e) => setErr(String(e.message || e)));
  useEffect(() => {
    void load();
    loadCatalogueContext(api, setCtx).then(setCtx).catch(() => setCtx(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, id]);
  if (err) return <p className="text-sm text-rose-700">{err}</p>;
  if (!product) return <p className="text-sm text-slate-500">Loading…</p>;
  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs text-slate-500"><Link className="hover:underline" to="/products">Products</Link> › {product.product_name || product.ean_code}</div>
        <h1 className="text-2xl font-semibold">{product.product_name || <span className="text-amber-700">no name</span>}</h1>
        <p className="font-mono text-xs text-slate-500">{product.id}</p>
      </div>
      <Details product={product} onSaved={load} mayWrite={mayWrite} />
      <Training product={product} ctx={ctx} />
      <Photos product={product} onChanged={load} mayWrite={mayWrite} />
      <Placements product={product} ctx={ctx} />
    </div>
  );
}
